/**
 * Request Footer Component
 *
 * @module @features/requests/components/RequestDetails/components/RequestFooter
 */

import React from 'react';
import { Button } from '@dynamon-as/ui';
import { Send } from '@mui/icons-material';
import { Tooltip as MUITooltip } from '@mui/material';
import { useI18n } from '@shared/i18n';
import type { Request } from '@features/requests/types';
import { RequestStatus } from '@features/requests/types';
import styles from '../RequestDetails.module.css';

/**
 * Tooltip styling aligned with Dynamon design tokens
 */
const tooltipStyle = {
    backgroundColor: 'var(--dyn-color-white)',
    color: 'var(--dyn-color-black-2)',
    border: '1px solid var(--dyn-color-gray-4)',
    fontSize: 'var(--dyn-text-sm-font-size)',
    borderRadius: 'var(--dyn-radius-sm)',
    padding: 'var(--dyn-space-1) var(--dyn-space-2)',
};

/**
 * Props for RequestFooter component
 */
export interface RequestFooterProps {
    request: Request;
    onBack: () => void;
    onEdit?: () => void;
    onSubmit?: () => void;
    onCancel?: () => void;
    hasSelectedDates: boolean;
}

/**
 * Displays action buttons for request
 */
export function RequestFooter({
    request,
    onBack,
    onEdit,
    onSubmit,
    onCancel,
    hasSelectedDates,
}: RequestFooterProps): React.ReactElement {
    const { t } = useI18n();

    const canEdit =
        request.status === RequestStatus.DRAFT ||
        request.status === RequestStatus.REJECTED;

    const canSubmit = request.status === RequestStatus.DRAFT;
    const canResubmit = request.status === RequestStatus.REJECTED;
    const canCancel = request.status === RequestStatus.SUBMITTED;

    return (
        <div className={styles.footer}>
            <Button
                text={t('requests.details.back')}
                color="secondary"
                variant="outlined"
                onClick={onBack}
            />
            {canEdit && onEdit && (
                <Button
                    text={t('requests.details.edit')}
                    color="primary"
                    variant="outlined"
                    onClick={onEdit}
                />
            )}
            {canSubmit && onSubmit && (
                <MUITooltip
                    title={
                        !hasSelectedDates
                            ? t('requests.actions.submitDisabledNoDates')
                            : ''
                    }
                    arrow
                    slotProps={{ tooltip: { style: tooltipStyle } }}
                >
                    <span>
                        <Button
                            text={t('requests.details.submit')}
                            color="success"
                            onClick={onSubmit}
                            Icon={(): React.ReactElement => (
                                <Send fontSize="small" />
                            )}
                            disabled={!hasSelectedDates}
                        />
                    </span>
                </MUITooltip>
            )}
            {canResubmit && onSubmit && (
                <MUITooltip
                    title={
                        !hasSelectedDates
                            ? t('requests.actions.submitDisabledNoDates')
                            : ''
                    }
                    arrow
                    slotProps={{ tooltip: { style: tooltipStyle } }}
                >
                    <span>
                        <Button
                            text={t('requests.details.resubmit')}
                            color="success"
                            onClick={onSubmit}
                            aria-label={t('requests.actions.resubmit')}
                            data-testid="request-resubmit-button"
                            Icon={(): React.ReactElement => (
                                <Send fontSize="small" />
                            )}
                            disabled={!hasSelectedDates}
                        />
                    </span>
                </MUITooltip>
            )}
            {canCancel && onCancel && (
                <Button
                    text={t('requests.details.cancel')}
                    color="danger"
                    variant="outlined"
                    onClick={onCancel}
                />
            )}
        </div>
    );
}
